import type { SignatureDetails, SignatureTemplate, BackgroundUserInfo } from "@/shared/types";
import type { ISignatureInserter, InsertResult } from "@/shared/types/inserter";
import { NOTIFICATION_IDS, showActionBar, showInfoBar, showErrorBar, removeAllNotifications } from "./notifications";
import { getTemplateName, getCachedTemplates, extractTemplateId } from "./template-cache";
import { renderSignature } from "./renderer";
import { templateCache } from "@/features/signature/services/template-cache";
import { refreshTemplatesIfStale } from "@/features/signature/services/api";
import { userDataCache } from "@/shared/services/user-data-cache";
import { info, warn, error, debug, flushNow } from "@/shared/services/logger";

/**
 * Tempo máximo (ms) de segurança para encerrar o launch event no mobile,
 * abaixo do limite de ~60s do Outlook Mobile, garantindo que o add-in nunca
 * pendure caso alguma etapa assíncrona não conclua.
 */
const MOBILE_TIMEOUT_MS = 55_000;

/**
 * Classe base abstrata para inserters de assinatura no Outlook.
 *
 * Implementa toda a lógica compartilhada de orquestração (validação de sessão,
 * resolução de template, renderização, anexo de logo inline e inserção).
 * Subclasses precisam apenas implementar {@link insertSignature}.
 *
 * @example
 * ```typescript
 * class OutlookClassicSignatureInserter extends BaseSignatureInserter {
 *   constructor() { super("OutlookClassicInserter"); }
 *   insertSignature(html, item) { ... }
 * }
 * ```
 */
export abstract class BaseSignatureInserter implements ISignatureInserter {
  /**
   * @param source - Identificador da subclasse para os logs.
   */
  constructor(protected readonly source: string) {}

  /**
   * Insere HTML de assinatura no corpo do item, usando a estratégia
   * específica da plataforma (Classic, New/Web ou Mobile).
   *
   * @param html - Conteúdo HTML da assinatura a ser inserida.
   * @param item - Item do Outlook (`Office.MessageCompose` ou `Office.AppointmentCompose`).
   * @returns Promise com o resultado da inserção.
   *
   * @example
   * const result = await inserter.insertSignature(html, item);
   */
  abstract insertSignature(html: string, item: Office.Item): Promise<InsertResult>;

  /**
   * Orquestração completa de auto-inserção: valida dados do usuário, resolve
   * template, renderiza, anexa logo e insere no corpo.
   *
   * @param event - Evento de comando do Office Add-in.
   *
   * @example
   * Office.actions.associate("checkSignature", (event) => inserter.insert(event));
   */
  insert(event: Office.AddinCommands.Event): void {
    info(this.source, "insert() — início da auto-inserção.");

    const platform = Office.context?.platform as unknown as string;
    const isMobile = platform === "Android" || platform === "iOS";

    // No Classic, event.completed() é chamado IMEDIATAMENTE para evitar o dialog
    // "add-in está demorando" (~5-7s): o runtime mantém o item acessível por
    // ~300s após completed(). No mobile, encerrar o evento no início corta a
    // inserção assíncrona — por isso o completed() só ocorre em finish().
    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      if (isMobile) {
        try { event.completed(); } catch { /* silencioso */ }
      }
      flushNow();
    };

    if (isMobile) {
      // Rede de segurança: nunca pendurar além do limite de ~60s do mobile.
      setTimeout(finish, MOBILE_TIMEOUT_MS);
    } else {
      event.completed();
    }

    const item = Office.context.mailbox.item;
    debug(this.source, `mailbox.item: ${item ? "disponível" : "NULO"}, itemType: ${(item as any)?.itemType}`);

    if (!item) {
      error(this.source, "Item não disponível — abortando.");
      finish();
      return;
    }

    const userInfo = this.#validateUserInfo();
    if (!userInfo) {
      showActionBar(
        "Habilite a assinatura automática para seus e-mails.",
        NOTIFICATION_IDS.CONFIGURE_NEEDED
      );
      finish();
      return;
    }

    removeAllNotifications(item);
    this.#notifySession();
    this.#refreshTemplatesInBackground(userInfo);

    if (item.getComposeTypeAsync) {
      debug(this.source, "getComposeTypeAsync disponível — detectando tipo de composição.");
      item.getComposeTypeAsync(
        { asyncContext: { userInfo } },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            const composeType = result.value.composeType;
            info(this.source, `[PATH] getComposeTypeAsync OK → composeType=${composeType}`);
            this.#insertAutoSignature(composeType, result.asyncContext.userInfo, finish);
          } else {
            warn(this.source, `[PATH] getComposeTypeAsync FALHOU (status=${result.status}) → assumindo newMail.`);
            this.#insertAutoSignature("newMail", result.asyncContext.userInfo, finish);
          }
        }
      );
    } else {
      info(this.source, "[PATH] getComposeTypeAsync indisponível → assumindo newMail.");
      this.#insertAutoSignature("newMail", userInfo, finish);
    }
  }

  /**
   * Lê e valida os dados do usuário do roaming settings.
   *
   * @returns Dados do usuário ou `null` se ausentes/inválidos.
   */
  #validateUserInfo(): BackgroundUserInfo | null {
    const raw = Office.context.roamingSettings.get("helpitech_user_info");
    const hasData = raw && raw !== "" && raw !== "null";
    debug(this.source, `helpitech_user_info presente: ${hasData}, tipo: ${typeof raw}`);

    if (!hasData) return null;

    try {
      const parsed: BackgroundUserInfo = typeof raw === "string" ? JSON.parse(raw) : raw as BackgroundUserInfo;
      if (!parsed || !parsed.name || !parsed.email) {
        throw new Error("Incomplete data");
      }
      info(this.source, `Dados do usuário válidos: name=${parsed.name}, email=${parsed.email}`);
      return parsed;
    } catch (err: any) {
      warn(this.source, `Dados do usuário inválidos: ${err?.message || "parse error"}`);
      return null;
    }
  }

  /**
   * Rebusca os templates na plataforma quando o cache passou dos 10 minutos.
   *
   * Roda sem `await`: a assinatura deste e-mail usa o cache atual de qualquer
   * forma, e o resultado só beneficia a próxima composição. É o que mantém as
   * assinaturas atualizadas para quem quase nunca abre o painel.
   *
   * @param userInfo - Dados do usuário usados para identificar o recipient.
   */
  #refreshTemplatesInBackground(userInfo: BackgroundUserInfo): void {
    // A identidade é o e-mail real da conta, nunca o `email` exibido, que o
    // usuário pode ter sobrescrito quando a edição está liberada.
    const identity = userDataCache.identityEmail();
    if (!identity || !templateCache.isStale()) return;

    debug(this.source, "Cache de templates vencido — rebuscando na plataforma.");
    void refreshTemplatesIfStale(identity, userInfo.name, false);
  }

  /**
   * Exibe a notificação de sessão gerenciada.
   */
  #notifySession(): void {
    info(this.source, "Exibindo notificação de assinatura gerenciada.");
    showInfoBar(
      "Sua assinatura é gerenciada pelo suplemento Helpitech Signature.",
      NOTIFICATION_IDS.MANAGED
    );
  }

  /**
   * Orquestra a inserção automática: resolve template, renderiza e insere no corpo.
   *
   * @param composeType - Tipo de composição (`newMail`, `reply`, `forward`).
   * @param userInfo - Dados do perfil do usuário.
   * @param finish - Callback de encerramento do launch event.
   */
  #insertAutoSignature(
    composeType: string,
    userInfo: BackgroundUserInfo,
    finish: () => void,
  ): void {
    info(this.source, `#insertAutoSignature → composeType=${composeType}`);

    const templateName = getTemplateName(composeType);
    info(this.source, `Template resolvido: ${templateName}`);

    if (templateName === "none" || !templateName) {
      info(this.source, "Nenhum template configurado (none) — abortando.");
      finish();
      return;
    }

    const item = Office.context.mailbox.item;
    if (!item) {
      error(this.source, "mailbox.item tornou-se nulo durante #insertAutoSignature.");
      finish();
      return;
    }

    const templates = getCachedTemplates();
    debug(this.source, `${templates.length} templates no cache.`);
    const templateId = extractTemplateId(templateName);
    const tpl = templates.find((t) => t.id === templateId);

    if (!tpl) {
      warn(this.source, `Template não encontrado: name=${templateName}, id=${templateId}, cacheSize=${templates.length}`);
      finish();
      return;
    }

    info(this.source, `Template encontrado: id=${tpl.id}, nome=${tpl.name || "?"}`);
    const details = renderSignature(tpl.html, userInfo);
    debug(this.source, `Assinatura renderizada: ${details.signature.length} chars, logo=${details.logoBase64 ? "sim" : "não"}`);

    this.#insertWithLogo(details, item, () => {
      info(this.source, "Inserção de assinatura concluída.");
      finish();
    });
  }

  /**
   * Anexa logo inline (se presente) e depois insere a assinatura.
   *
   * @param details - Detalhes da assinatura renderizada.
   * @param item - Item do Outlook.
   * @param onDone - Callback chamado após conclusão.
   */
  #insertWithLogo(
    details: SignatureDetails,
    item: Office.Item,
    onDone: () => void
  ): void {
    if (details.logoBase64 && details.logoFileName) {
      info(this.source, `[PATH] Logo presente (${details.logoFileName}, ${details.logoBase64.length} chars) → anexando.`);
      (item as any).addFileAttachmentFromBase64Async(
        details.logoBase64,
        details.logoFileName,
        { isInline: true },
        (result: Office.AsyncResult<string>) => {
          if (result.status !== Office.AsyncResultStatus.Succeeded) {
            warn(this.source, `[PATH] Falha ao anexar logo: ${result.error?.message} → prosseguindo sem logo.`);
          } else {
            info(this.source, "[PATH] Logo inline anexado com sucesso.");
          }
          this.#delegateInsert(details, item, onDone);
        }
      );
    } else {
      info(this.source, "[PATH] Sem logo → inserindo assinatura direto.");
      this.#delegateInsert(details, item, onDone);
    }
  }

  /**
   * Delega para o método de inserção adequado (mensagem vs compromisso).
   *
   * @param details - Detalhes da assinatura renderizada.
   * @param item - Item do Outlook.
   * @param onDone - Callback chamado após conclusão.
   */
  #delegateInsert(
    details: SignatureDetails,
    item: Office.Item,
    onDone: () => void
  ): void {
    if ((item as any).itemType === Office.MailboxEnums.ItemType.Appointment) {
      info(this.source, `[PATH] Compromisso → setBody (${details.signature.length} chars).`);
      this.#setAppointmentBody(item, details, onDone);
    } else {
      info(this.source, `[PATH] Mensagem → insertSignature (${details.signature.length} chars).`);
      this.insertSignature(details.signature, item).then((result) => {
        if (!result.success) {
          error(this.source, `insertSignature falhou: ${result.errorMessage} (code=${result.errorCode})`);
          showErrorBar(
            "Não foi possível inserir sua assinatura automaticamente. Reabra a janela de composição ou tente novamente.",
            NOTIFICATION_IDS.INSERT_FAILED
          );
        }
        onDone();
      });
    }
  }

  /**
   * Define o corpo de um compromisso com a assinatura renderizada.
   *
   * @param item - Item de compromisso do Outlook.
   * @param details - Detalhes da assinatura renderizada.
   * @param onDone - Callback chamado após conclusão.
   */
  #setAppointmentBody(
    item: Office.Item,
    details: SignatureDetails,
    onDone: () => void
  ): void {
    const html = "<br/>" + details.signature;
    (item as any).body.setAsync(html, { coercionType: Office.CoercionType.Html }, (r: Office.AsyncResult<void>) => {
      if (r.status === Office.AsyncResultStatus.Succeeded) {
        info(this.source, "setAsync (appointment) OK.");
      } else {
        error(this.source, `setAsync (appointment) falhou — code=${r.error?.code}, msg=${r.error?.message}`);
      }
      onDone();
    });
  }
}
